import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import { gray } from '../../shared-theme/themePrimitives';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import TourIcon from '@mui/icons-material/Tour';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PublicIcon from '@mui/icons-material/Public';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import { useNavigate, useParams } from 'react-router';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import { useNotification } from './CustomNotification';
import {
  deleteOne as deleteOffer,
  getOne as getOffer,
  type Offer,
} from '../data/offers';
import PageContainer from './PageContainer';
import {useLanguage} from "../../contexts/LanguageContext";
import { ArrowForward } from '@mui/icons-material';
import {useAuth} from "../../contexts/AuthContext";

// Attribute Display Component
interface AttributeItemProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  isRtl?: boolean;
  forceLtr?: boolean;
}

function AttributeItem({ icon, label, value, isRtl = false, forceLtr = false }: AttributeItemProps) {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2.5,
        borderRadius: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        ...theme.applyStyles('dark', {
          backgroundColor: 'hsl(220, 25%, 20%)',
          border: `1px solid ${alpha(gray[500], 0.3)}`,
        }),
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          ...theme.applyStyles('dark', {
            backgroundColor: 'hsl(220, 25%, 22%)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          }),
        },
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexDirection: isRtl ? 'row-reverse' : 'row',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            color: 'white',
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            textAlign: isRtl ? 'right' : 'left',
            flex: 1,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          color: 'text.primary',
          textAlign: isRtl ? 'right' : 'left',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          pl: isRtl ? 0 : 7,
          pr: isRtl ? 7 : 0,
        }}
      >
        {forceLtr ? <span dir="ltr">{value}</span> : value}
      </Typography>
    </Box>
  );
}

// Image Gallery Component
function ImageGallery({ images }: { images: string[] }) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isHovered, setIsHovered] = React.useState(false);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (!images || images.length === 0) {
    return (
      <Paper
        sx={{
          height: 400,
          backgroundColor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No images available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{ position: 'relative', overflow: 'hidden', width: '70%', aspectRatio: '16 / 9', mx: 'auto' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        component="img"
        src={images[currentIndex]}
        alt={`Image ${currentIndex + 1}`}
        sx={{
          width: '100%',
          aspectRatio: '16 / 9',
          backgroundColor: 'grey.100',
        }}
      />
      {images.length > 1 && (
        <>
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              opacity: isHovered ? 0.6 : 0,
              transition: 'opacity 0.3s ease',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              opacity: isHovered ? 0.6 : 0,
              transition: 'opacity 0.3s ease',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 2,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          >
            <Typography variant="body2">
              {currentIndex + 1} / {images.length}
            </Typography>
          </Box>
          {/* Thumbnail navigation */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 60,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              p: 1,
              borderRadius: 1,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          >
            {images.map((image, index) => (
              <Box
                key={index}
                component="img"
                src={image}
                alt={`Thumbnail ${index + 1}`}
                onClick={() => setCurrentIndex(index)}
                sx={{
                  width: 60,
                  height: 60,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  border: index === currentIndex ? '3px solid white' : '3px solid transparent',
                  borderRadius: 1,
                  opacity: index === currentIndex ? 1 : 0.6,
                  transition: 'all 0.2s',
                  '&:hover': { opacity: 1 },
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
}

export default function OfferShow() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dialogs = useDialogs();
  const notifications = useNotification();

  const [offer, setOffer] = React.useState<Offer | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const showData = await getOffer(Number(offerId));

      showData.gallery_image_urls = showData.gallery_image_urls?.length > 0 ? showData.gallery_image_urls : ["https://ichef.bbci.co.uk/ace/standard/976/cpsprodpb/14235/production/_100058428_mediaitem100058424.jpg", "https://i0.wp.com/picjumbo.com/wp-content/uploads/beautiful-fall-nature-scenery-free-image.jpeg?w=2210&quality=70", "https://thumbs.dreamstime.com/b/young-conceptual-image-large-stone-shape-human-brain-conceptual-image-large-stone-shape-110748113.jpg"];
      setOffer(showData);
    } catch (showDataError) {
      setError(showDataError as Error);
    }
    setIsLoading(false);
  }, [offerId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOfferEdit = React.useCallback(() => {
    navigate(`/offers/${offerId}/edit`);
  }, [navigate, offerId]);

  const handleOfferDelete = React.useCallback(async () => {
    if (!offer) {
      return;
    }

    const confirmed = await dialogs.confirm(
      t('offers.deletePopup.deleteOfferMessage'),
      {
        title: t('offers.deletePopup.deleteOffer'),
        severity: 'error',
        okText: t('offers.deletePopup.confirm'),
        cancelText: t('offers.deletePopup.cancel'),
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        await deleteOffer(Number(offerId));

        navigate('/offers');

        notifications.show('Offer deleted successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });
      } catch (deleteError) {
        notifications.show(
          `Failed to delete offer. Reason: ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [offer, dialogs, offerId, navigate, notifications]);

  const handleBack = React.useCallback(() => {
    navigate('/offers');
  }, [navigate]);

  const { t, direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const renderShow = React.useMemo(() => {

    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }
    return offer ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Stack spacing={4}>
          {/* Title Section */}
          <Paper sx={{ px: 3, py: 2, backgroundColor: 'transparent'}}>
            <Typography variant="h4" component="h1" gutterBottom>
              {offer.country && (
                <>
                  {offer.country.name}
                  <Box component="span" sx={{ mx: 2, color: 'text.secondary' }}>|</Box>
                </>
              )}
              {offer.title}
            </Typography>
          </Paper>

          {/* Images Gallery Section */}
          <Box>
            <ImageGallery images={offer.gallery_image_urls || []} />
          </Box>

            {/* Sales Description Section */}
            <Paper
                sx={(theme) => ({
                    px: 4,
                    py: 3,
                    borderRadius: 3,
                    backgroundColor: gray[50],
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    ...theme.applyStyles('dark', {
                        backgroundColor: 'hsl(220, 25%, 18%)',
                        borderColor: alpha(gray[500], 0.4),
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                    }),
                })}
            >
                <Typography
                    variant="h6"
                    sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: 'primary.main',
                        textAlign: isRtl ? 'right' : 'left',
                    }}
                >
                    {t('offers.salesDescription')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography
                    variant="body1"
                    sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        textAlign: isRtl ? 'right' : 'left',
                        lineHeight: 1.8,
                        color: 'text.secondary',
                    }}
                >
                    {offer.description}
                </Typography>
            </Paper>

          {/* Cost Description Section */}
          <Paper
            sx={(theme) => ({
              px: 4,
              py: 3,
              borderRadius: 3,
              backgroundColor: gray[50],
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              ...theme.applyStyles('dark', {
                backgroundColor: 'hsl(220, 25%, 18%)',
                borderColor: alpha(gray[500], 0.4),
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              }),
            })}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: 'primary.main',
                textAlign: isRtl ? 'right' : 'left',
              }}
            >
              {t('offers.costDescription')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                textAlign: isRtl ? 'right' : 'left',
                lineHeight: 1.8,
                color: 'text.secondary',
              }}
            >
              {offer.cost_description}
            </Typography>
          </Paper>

          {/* Provider Information Section */}
          {(offer.provider_name || offer.provider_phone_number || offer.seats_total !== undefined) && (
            <Paper
              sx={(theme) => ({
                px: 4,
                py: 3,
                borderRadius: 3,
                backgroundColor: gray[50],
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                ...theme.applyStyles('dark', {
                  backgroundColor: 'hsl(220, 25%, 18%)',
                  borderColor: alpha(gray[500], 0.4),
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                }),
              })}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: 'primary.main',
                  textAlign: isRtl ? 'right' : 'left',
                }}
              >
                  {t('offers.providerInformation')}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {offer.provider_name && (
                  <Grid  size={{ xs: 12, sm: 6, md: 4 }} >
                    <AttributeItem
                      icon={<PersonIcon />}
                      label={t('offers.providerName')}
                      value={offer.provider_name}
                      isRtl={isRtl}
                    />
                  </Grid>
                )}
                {offer.provider_phone_number && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <AttributeItem
                      icon={<PhoneIcon />}
                      label={t('offers.providerPhoneNumber')}
                      value={offer.provider_phone_number}
                      isRtl={isRtl}
                      forceLtr
                    />
                  </Grid>
                )}
                {offer.seats_total !== undefined && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <AttributeItem
                      icon={<EventSeatIcon />}
                      label={t('offers.seatsNumber')}
                      value={offer.seats_total}
                      isRtl={isRtl}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* Action Buttons */}
            { (user?.role === 'GM' || user?.role === 'BM') ? (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            { (user?.role === 'GM' || user?.role === 'BM') && (
              <Stack direction="row" >
                <RtlButton
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleOfferEdit}
                >
                    {t('common.edit')}
                </RtlButton>
                <RtlButton
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleOfferDelete}
                  sx={{
                      marginRight:  `${isRtl? 8 : 0 }px`,
                      marginLeft:  `${isRtl? 0 : 8 }px`,
                      '&:hover': { backgroundColor: 'rgb(110,19,19)' },
                  }}
                >
                    {t('common.delete')}
                </RtlButton>
              </Stack>
            )}
          </Stack>
            ): null}
        </Stack>
      </Box>
    ) : null;
  }, [
    isLoading,
    error,
    offer,
    handleBack,
    handleOfferEdit,
    handleOfferDelete,
  ]);

  const pageTitle = `Offer ${offerId}`;

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('offers.title'), path: '/offers' },
        { title: t('offers.view') },
      ]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={{
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: '#31394a',
              color: 'white'
            },
          }}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}

